import React, { useContext } from 'react'
import { useGlobals } from 'context'
import { useQuery, useMutation } from '@apollo/client'
import { GET_DEPARTMENTS } from '../queries'
import { CHANGE_DEPARTMENT, REMOVE_DEPARTMENT } from '../mutations'
import { Trans } from '@lingui/macro'

const DepartmentContext = React.createContext()

function DepartmentProvider({ children }) {
  const { createError, createSuccess } = useGlobals()
  const { loading, data, refetch } = useQuery(GET_DEPARTMENTS)
  const { departments } = data || {}

  const newUpdate = (cache, { data: { changeDepartment } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_DEPARTMENTS,
      })
      cache.writeQuery({
        query: GET_DEPARTMENTS,
        data: { departments: [...cached.departments, changeDepartment] },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const updateExisting = (cache, { data: { changeDepartment } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_DEPARTMENTS,
      })
      cache.writeQuery({
        query: GET_DEPARTMENTS,
        data: {
          departments: cached.departments.map((department) => {
            if (department.department_id === changeDepartment.department_id) {
              return {
                ...department,
                ...changeDepartment,
              }
            }
            return department
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const removeExisting = (cache, { data: { removeDepartment } }) => {
    try {
      const cached = cache.readQuery({
        query: GET_DEPARTMENTS,
      })
      cache.writeQuery({
        query: GET_DEPARTMENTS,
        data: {
          departments: cached.departments.filter((department) => {
            return department.department_id !== removeDepartment.department_id
          }),
        },
      })
    } catch (error) {
      // Event was not in cache
    }
  }

  const [newDepartment] = useMutation(CHANGE_DEPARTMENT, {
    update: newUpdate,
  })

  const [updateDepartment] = useMutation(CHANGE_DEPARTMENT, {
    update: updateExisting,
  })

  const [removeDepartment] = useMutation(REMOVE_DEPARTMENT, {
    update: removeExisting,
  })

  async function create(input) {
    try {
      const response = await newDepartment({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Department</Trans>, 'create')
      return response.data?.changeDepartment
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add department</Trans>,
      )
    }
  }

  async function update(input) {
    try {
      const response = await updateDepartment({
        variables: {
          input,
        },
      })
      createSuccess(<Trans>Department</Trans>, 'update')
      return response.data?.changeDepartment
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to update department</Trans>,
      )
    }
  }

  async function remove(department_id) {
    try {
      const response = await removeDepartment({
        variables: {
          department_id,
        },
      })
      createSuccess(<Trans>Department</Trans>, 'delete')
      return response.data?.removeDepartment
    } catch (error) {
      createError(
        error.graphQLErrors[0].message,
        <Trans>Unable to add remove department</Trans>,
      )
    }
  }

  return (
    <DepartmentContext.Provider
      value={{
        loading,
        departments,
        refetch,
        create,
        update,
        remove,
      }}
    >
      {children}
    </DepartmentContext.Provider>
  )
}

function useDepartments() {
  return useContext(DepartmentContext)
}

export { DepartmentProvider, useDepartments }
